const chapter = {
  id: 'magnetism-matter',
  slug: 'magnetism-matter',
  subject: 'physics',
  subjectLabel: 'Physics',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Magnetism & Matter',
        hindi: 'चुम्बकत्व और द्रव्य',
  },
  estReadMinutes: 45,
  difficulty: {
    english: 'Easy',
        hindi: 'आसान',
  },
  weightage: {
    english: '1 question in JEE Main / NEET',
        hindi: 'जेईई मेन/एनईईटी में 1 प्रश्न',
  },
  syllabusTags: ['Electromagnetism', 'JEE-Main', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'bar-magnet',
      title: {
        english: 'Bar Magnet & Magnetic Dipole',
        hindi: 'छड़ चुम्बक और चुम्बकीय द्विध्रुव',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'A bar magnet is a magnetic dipole with moment $\\vec m$ from S to N. Pole strength $m_p$, length $2\\ell$ (effective): $m = m_p \\cdot 2\\ell$.',
        hindi: 'एक बार चुंबक एक चुंबकीय द्विध्रुव है जिसका क्षण $\\vec m$ S से N तक होता है। ध्रुव शक्ति $m_p$, लंबाई $2\\ell$ (प्रभावी): $m = m_p \\cdot 2\\ell$। **अक्षीय क्षेत्र** दूरी $\\vec m$ पर: $m_p$ ($2\\ell$)।',
            },
            {
              english: '**Axial field** at distance $r$: $B_{\\text{ax}} = \\dfrac{\\mu_0}{4\\pi}\\dfrac{2m}{r^3}$ ($r \\gg 2\\ell$).',
        hindi: '**अक्षीय क्षेत्र** दूरी $r$ पर: $B_{\\text{ax}} = \\dfrac{\\mu_0}{4\\pi}\\dfrac{2m}{r^3}$ ($r \\gg 2\\ell$)।',
            },
            {
              english: '**Equatorial field**: $B_{\\text{eq}} = \\dfrac{\\mu_0}{4\\pi}\\dfrac{m}{r^3}$.',
        hindi: '**भूमध्यरेखीय क्षेत्र**: $B_{\\text{eq}} = \\dfrac{\\mu_0}{4\\pi}\\dfrac{m}{r^3}$.',
            },
            {
              english: '**Torque** in field $\\vec B$: $\\vec\\tau = \\vec m \\times \\vec B$, magnitude $mB\\sin\\theta$.',
        hindi: '**टॉर्क** फ़ील्ड $\\vec B$ में: $\\vec\\tau = \\vec m \\times \\vec B$, परिमाण $mB\\sin\\theta$।',
            },
            {
              english: '**PE**: $U = -\\vec m \\cdot \\vec B = -mB\\cos\\theta$.',
        hindi: '**PE**: $U = -\\vec m \\cdot \\vec B = -mB\\cos\\theta$.',
            },
            {
              english: 'Time period of oscillating bar magnet: $T = 2\\pi\\sqrt{I/(mB)}$, $I$ = moment of inertia.',
        hindi: 'दोलनशील छड़ चुंबक की समयावधि: $T = 2\\pi\\sqrt{I/(mB)}$, $I$ = जड़त्व आघूर्ण।',
            },
          ],
        },
        {
          type: 'tip',
          text: {
            english: 'Gauss\'s law for magnetism: $\\oint \\vec B \\cdot d\\vec A = 0$. **Magnetic monopoles do not exist** (in classical physics).',
        hindi: 'चुंबकत्व के लिए गॉस का नियम: $\\oint \\vec B \\cdot d\\vec A = 0$। **चुंबकीय मोनोपोल मौजूद नहीं हैं** (शास्त्रीय भौतिकी में)।',
          },
        },
      ],
    },
    {
      id: 'earth-mag',
      title: {
        english: 'Earth\'s Magnetic Field',
        hindi: 'पृथ्वी का चुंबकीय क्षेत्र',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Earth behaves like a giant dipole tilted ~$11.3°$ from rotation axis. Magnetic poles are opposite (geomagnetic N pole is near geographic S pole and vice versa, since N tip of compass points N).',
        hindi: 'पृथ्वी घूर्णन अक्ष से ~$11.3°$ झुकी हुई एक विशाल द्विध्रुव की तरह व्यवहार करती है। चुंबकीय ध्रुव विपरीत हैं (जियोमैग्नेटिक एन ध्रुव भौगोलिक एस ध्रुव के पास है और इसके विपरीत, क्योंकि एन कम्पास बिंदु एन की नोक है)।',
            },
            {
              english: 'Three elements: **declination** $D$ (angle between geographic & magnetic meridian), **dip** (inclination) $\\delta$, **horizontal component** $B_H$.',
        hindi: 'तीन तत्व: **झुकाव** $D$ (भौगोलिक और चुंबकीय मेरिडियन के बीच का कोण), **डुबकी** (झुकाव) $\\delta$, **क्षैतिज घटक** $B_H$।',
            },
            {
              english: 'Total intensity $B = B_H/\\cos\\delta = B_V/\\sin\\delta$. Vertical component $B_V = B\\sin\\delta$.',
        hindi: 'कुल तीव्रता $B = B_H/\\cos\\delta = B_V/\\sin\\delta$. लंबवत घटक $B_V = B\\sin\\delta$.',
            },
            {
              english: 'At magnetic equator, $\\delta = 0°$ (horizontal field only). At magnetic poles, $\\delta = 90°$.',
        hindi: 'चुंबकीय भूमध्य रेखा पर, $\\delta = 0°$ (केवल क्षैतिज क्षेत्र)। चुंबकीय ध्रुवों पर, $\\delta = 90°$.',
            },
          ],
        },
      ],
    },
    {
      id: 'magnetisation',
      title: {
        english: 'Magnetisation, $\\vec H$ and Susceptibility',
        hindi: 'चुंबकत्व, $\\vec H$ और सुग्राह्यता',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Intensity of magnetisation',
        hindi: 'चुम्बकत्व की तीव्रता',
          },
          body: {
            english: '$\\vec M$ = net dipole moment per unit volume. Unit A/m. **Magnetic intensity** $\\vec H$ defined by $\\vec B = \\mu_0(\\vec H + \\vec M)$. In linear media: $\\vec M = \\chi_m \\vec H$, $\\vec B = \\mu \\vec H$, $\\mu = \\mu_0\\mu_r$, $\\mu_r = 1 + \\chi_m$.',
        hindi: '$\\vec M$ = प्रति इकाई आयतन शुद्ध द्विध्रुव आघूर्ण। यूनिट ए/एम. **चुंबकीय तीव्रता** $\\vec H$ $\\vec B = \\mu_0(\\vec H + \\vec M)$ द्वारा परिभाषित। रैखिक मीडिया में: $\\vec M = \\chi_m \\vec H$, $\\vec B = \\mu \\vec H$, $\\mu = \\mu_0\\mu_r$, $\\mu_r = 1 + \\chi_m$।',
          },
        },
      ],
    },
    {
      id: 'classification',
      title: {
        english: 'Diamagnetic, Paramagnetic & Ferromagnetic',
        hindi: 'प्रतिचुम्बकीय, अनुचुम्बकीय और लौहचुम्बकीय',
      },
      blocks: [
        {
          type: 'table',
          headers: ['Property', 'Diamagnetic', 'Paramagnetic', 'Ferromagnetic'],
          rows: [
            ['$\\chi_m$', 'small, negative', 'small, positive', 'large, positive'],
            ['$\\mu_r$', '$<1$', '$>1$ (slightly)', '$\\gg 1$'],
            ['Behaviour in non-uniform field', 'repelled, moves to weak field', 'attracted to strong field', 'strongly attracted'],
            ['Temperature dependence', 'almost independent', '$\\chi_m \\propto 1/T$ (Curie\'s law)', 'complex; loses ferro above Curie temp $T_C$ → para'],
            ['Examples', 'Bi, Cu, H₂O, NaCl', 'Al, O₂, Pt, CuCl₂', 'Fe, Ni, Co, Gd'],
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Curie\'s law (paramagnets)',
        hindi: 'क्यूरी का नियम (पैरामैग्नेट)',
          },
          statement: {
            english: '$\\chi_m = C/T$, where $C$ = Curie constant.',
        hindi: '$\\chi_m = C/T$, जहां $C$ = क्यूरी स्थिरांक। *फेरोमैग्नेट के लिए **हिस्टैरिसीस लूप**: $\\chi_m = C/T$-vs-$C$ धारणशीलता ( पर अवशिष्ट चुंबकत्व) और जबरदस्ती (विचुंबकीकरण के लिए विपरीत क्षेत्र) दिखाता है।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Hysteresis loop** for ferromagnet: $B$-vs-$H$ shows retentivity $B_r$ (residual magnetism at $H = 0$) and coercivity $H_c$ (reverse field to demagnetise).',
        hindi: '**हिस्टैरिसीस लूप** फेरोमैग्नेट के लिए: $B$-vs-$H$ प्रतिधारणशीलता $B_r$ ($H = 0$ पर अवशिष्ट चुंबकत्व) और जबरदस्ती $H_c$ (विचुंबकीकरण के लिए विपरीत क्षेत्र) दिखाता है।',
            },
            {
              english: 'Soft iron: low coercivity, narrow loop → electromagnets, transformer cores. Steel: high coercivity → permanent magnets.',
        hindi: 'नरम लोहा: कम जबरदस्ती, संकीर्ण लूप → विद्युत चुम्बक, ट्रांसफार्मर कोर। स्टील: उच्च जबरदस्ती → स्थायी चुंबक।',
            },
            {
              english: 'Energy lost per cycle of magnetisation = area of $B$–$H$ loop × volume.',
        hindi: 'magnetisation = area of – loop × volume के प्रति चक्र में ऊर्जा की हानि होती है।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'B_{\\text{ax}} = \\mu_0(2m)/(4\\pi r^3)',
      desc: {
        english: 'Bar magnet axial',
        hindi: 'बार चुंबक अक्षीय',
      },
    },
    {
      latex: 'B_{\\text{eq}} = \\mu_0 m/(4\\pi r^3)',
      desc: {
        english: 'Equatorial',
        hindi: 'भूमध्यरेखीय',
      },
    },
    {
      latex: '\\vec\\tau = \\vec m\\times\\vec B,\\ U = -\\vec m\\cdot\\vec B',
      desc: {
        english: 'Torque & PE',
        hindi: 'टॉर्क और पीई',
      },
    },
    {
      latex: 'B = B_H/\\cos\\delta',
      desc: {
        english: 'Earth field',
        hindi: 'पृथ्वी क्षेत्र',
      },
    },
    {
      latex: '\\vec B = \\mu_0(\\vec H + \\vec M)',
      desc: {
        english: 'Definition of H',
        hindi: 'एच की परिभाषा',
      },
    },
    {
      latex: '\\mu_r = 1 + \\chi_m',
      desc: {
        english: 'Permeability',
        hindi: 'पारगम्यता',
      },
    },
    {
      latex: '\\chi_m = C/T',
      desc: {
        english: 'Curie\'s law',
        hindi: 'क्यूरी का नियम',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Earth field',
      problem: {
        english: 'At a place, $B_H = 0.3$ G and dip angle $30°$. Total earth\'s field?',
        hindi: 'एक स्थान पर, $B_H = 0.3$ G और नमन कोण $30°$। पृथ्वी का कुल क्षेत्रफल?',
      },
      answer: {
        english: '$\\approx 0.346$ G',
        hindi: '$\\approx 0.346$ जी',
      },
      solution: {
        english: '$B = B_H/\\cos 30° = 0.3/(\\sqrt 3/2) = 0.6/\\sqrt 3 \\approx 0.346$ G.',
        hindi: '$B = B_H/\\cos 30° = 0.3/(\\sqrt 3/2) = 0.6/\\sqrt 3 \\approx 0.346$ जी.',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Materials',
      problem: {
        english: 'Which of the following is paramagnetic? (Bi, Cu, Al, NaCl)',
        hindi: 'निम्नलिखित में से कौन सा अनुचुंबकीय है? (बीआई, सीयू, अल, NaCl)',
      },
      answer: {
        english: 'Al',
        hindi: 'अल',
      },
      solution: {
        english: 'Bi/Cu/NaCl are diamagnetic; Al is paramagnetic.',
        hindi: 'Bi/Cu/NaCl प्रतिचुंबकीय हैं; अल अनुचुम्बकीय है।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Bar magnet',
      problem: {
        english: 'Bar magnet of moment 2 A·m² in field 0.1 T at $60°$ to field. Torque?',
        hindi: 'क्षेत्र में 0.1 T पर $60°$ पर 2 A·m² क्षण का बार चुंबक। टॉर्क?',
      },
      answer: {
        english: '$0.173$ N·m',
        hindi: '$0.173$ N·m',
      },
      solution: {
        english: '$\\tau = mB\\sin\\theta = 2\\cdot 0.1\\cdot \\sin 60° = 0.1732$.',
        hindi: '$\\tau = mB\\sin\\theta = 2\\cdot 0.1\\cdot \\sin 60° = 0.1732$.',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Curie',
      problem: {
        english: 'A paramagnet has $\\chi = 6\\times 10^{-3}$ at 300 K. At 600 K, $\\chi = ?$',
        hindi: 'एक पैरामैग्नेट में 300 K पर $\\chi = 6\\times 10^{-3}$ है। 600 K पर, $\\chi =?$',
      },
      answer: {
        english: '$3\\times 10^{-3}$',
        hindi: '$3\\times 10^{-3}$',
      },
      solution: {
        english: '$\\chi \\propto 1/T$.',
        hindi: '$\\chi \\propto 1/T$।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Hysteresis',
      problem: {
        english: 'For a soft magnetic material the hysteresis loop is __ and coercivity is __.',
        hindi: 'एक नरम चुंबकीय सामग्री के लिए हिस्टैरिसीस लूप __ है और जबरदस्ती __ है।',
      },
      answer: {
        english: 'narrow ; small',
        hindi: 'संकीर्ण; छोटा',
      },
      solution: {
        english: 'Soft iron: easy to magnetise/demagnetise.',
        hindi: 'नरम लोहा: magnetise/demagnetise करने में आसान।',
      },
    },
  ],
  next: {
    slug: 'emi-ac',
    title: 'Electromagnetic Induction & AC',
  },
};

export default chapter;
